import { TradingMode } from '@/types/agent';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface AgentState {
  setIsTradingModeChanging: (isChanging: boolean) => void;
  isTradingModeChanging: boolean;

  tradingMode: TradingMode;
  setTradingMode: (mode: TradingMode) => void;
  setAgentAddress: (userAddr: string, address: string | undefined | null) => void;
  setIsEnableAgentMode: (userAddr: string, isEnable: boolean) => void;

  setJWTToken: (userAddr: string, token: string | undefined) => void;

  userAgentSettings: {
    [userAddr: string]: {
      agentAddress?: string | null;
      isEnableAgentMode?: boolean;
      jwtToken?: {
        token: string;
        expiresAt: number;
      };
    };
  };
  resetAgentSettings: () => void;
}

function isTokenExpired(token: { token: string; expiresAt: number }): boolean {
  return token.expiresAt < Date.now();
}

// 7 days
const TOKEN_EXPIRES_IN = 1000 * 60 * 60 * 24 * 30;

export const useAgentStore = create<AgentState>()(
  devtools(
    persist(
      (set) => ({
        tradingMode: TradingMode.Classic,
        setTradingMode: (mode: TradingMode) => set({ tradingMode: mode }),
        setIsTradingModeChanging: (isChanging: boolean) => set({ isTradingModeChanging: isChanging }),
        isTradingModeChanging: false,
        setJWTToken: (userAddr: string, token: string | undefined) =>
          set((state) => ({
            userAgentSettings: {
              ...state.userAgentSettings,
              [userAddr]: {
                ...state.userAgentSettings[userAddr],
                jwtToken: token ? { token, expiresAt: Date.now() + TOKEN_EXPIRES_IN } : undefined,
              },
            },
          })),
        setIsEnableAgentMode: (userAddr: string, isEnable: boolean) =>
          set((state) => ({
            userAgentSettings: {
              ...state.userAgentSettings,
              [userAddr]: {
                ...state.userAgentSettings[userAddr],
                isEnableAgentMode: isEnable,
              },
            },
          })),
        setAgentAddress: (userAddr: string, address: string | undefined | null) =>
          set((state) => ({
            userAgentSettings: {
              ...state.userAgentSettings,
              [userAddr]: {
                ...state.userAgentSettings[userAddr],
                agentAddress: address,
              },
            },
          })),
        userAgentSettings: {},
        resetAgentSettings: () => set({ userAgentSettings: {} }),
      }),
      {
        name: 'agent-storage-1',
        partialize: (state) => ({
          userAgentSettings: state.userAgentSettings,
        }),
        version: 2,
        onRehydrateStorage: () => {
          // Triggered when data is restored from storage
          return (rehydratedState) => {
            // rehydratedState is the data restored from storage
            if (rehydratedState?.userAgentSettings) {
              setTimeout(() => {
                // Check JWT tokens for all users in storage
                Object.entries(rehydratedState.userAgentSettings).forEach(([userAddr, settings]) => {
                  if (!settings.jwtToken) return;

                  if (isTokenExpired(settings.jwtToken)) {
                    // Clear expired token and agent address for this user
                    useAgentStore.getState().setJWTToken(userAddr, undefined);
                    useAgentStore.getState().setAgentAddress(userAddr, undefined);
                    useAgentStore.getState().setIsEnableAgentMode(userAddr, false);
                  }
                });

                // If all tokens are expired, clear storage
                const allTokensExpired = Object.values(rehydratedState.userAgentSettings).every(
                  (settings) => !settings.jwtToken || isTokenExpired(settings.jwtToken),
                );
                if (allTokensExpired) {
                  // localStorage.removeItem('agent-storage');
                  useAgentStore.getState().resetAgentSettings();
                }
              }, 0);
            }
          };
        },
      },
    ),
    { name: 'agent-store' },
  ),
);
